import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
	type CreateRdoScheduledServiceInput,
	createRdoScheduledService,
	deleteRdoScheduledService,
	reorderRdoScheduledServices,
	type UpdateRdoScheduledServiceInput,
	updateRdoScheduledService,
} from "@/adapters/api";
import type { Rdo } from "../types.ts";
import { toRdoScheduledService } from "../utils/map.ts";
import { rdoQueryKey } from "./use-rdo.ts";

export function useRdoScheduledServices(
	constructionId: string | undefined,
	rdoId: string | undefined,
) {
	const queryClient = useQueryClient();
	const key = rdoQueryKey(constructionId, rdoId);

	function requireIds() {
		if (!constructionId || !rdoId) throw new Error("missing rdo");
		return { constructionId, rdoId };
	}

	function invalidate() {
		queryClient.invalidateQueries({ queryKey: key });
	}

	const create = useMutation({
		mutationFn: async (input: CreateRdoScheduledServiceInput) => {
			const ids = requireIds();
			return toRdoScheduledService(
				await createRdoScheduledService(ids.constructionId, ids.rdoId, input),
			);
		},
		onSuccess: invalidate,
	});

	const update = useMutation({
		mutationFn: async (variables: {
			serviceId: string;
			input: UpdateRdoScheduledServiceInput;
		}) => {
			const ids = requireIds();
			return toRdoScheduledService(
				await updateRdoScheduledService(
					ids.constructionId,
					ids.rdoId,
					variables.serviceId,
					variables.input,
				),
			);
		},
		onSuccess: (service) => {
			queryClient.setQueryData<Rdo>(key, (current) =>
				current
					? {
							...current,
							scheduledServices: current.scheduledServices.map((item) =>
								item.id === service.id ? service : item,
							),
						}
					: current,
			);
		},
		onError: invalidate,
	});

	const remove = useMutation({
		mutationFn: async (serviceId: string) => {
			const ids = requireIds();
			await deleteRdoScheduledService(ids.constructionId, ids.rdoId, serviceId);
		},
		onSuccess: invalidate,
	});

	const reorder = useMutation({
		mutationFn: async (orderedIds: string[]) => {
			const ids = requireIds();
			return (
				await reorderRdoScheduledServices(
					ids.constructionId,
					ids.rdoId,
					orderedIds,
				)
			).map(toRdoScheduledService);
		},
		onMutate: async (orderedIds) => {
			await queryClient.cancelQueries({ queryKey: key });
			const previous = queryClient.getQueryData<Rdo>(key);
			if (previous) {
				const byId = new Map(
					previous.scheduledServices.map((service) => [service.id, service]),
				);
				queryClient.setQueryData<Rdo>(key, {
					...previous,
					scheduledServices: orderedIds.flatMap((id) => {
						const service = byId.get(id);
						return service ? [service] : [];
					}),
				});
			}
			return { previous };
		},
		onError: (_error, _ids, context) => {
			if (context?.previous) queryClient.setQueryData(key, context.previous);
		},
		onSettled: invalidate,
	});

	return { create, update, remove, reorder };
}
